/**
 * Pure query-string builder for `GET /api/admin/ledger` (API-FIN-5).
 *
 * Empty / default values are omitted so the URL stays minimal and lines up
 * with the React Query cache key. Always emits `limit` (the BO page size is
 * smaller than the API default of 100). Returns a string starting with `?`,
 * or `?limit=…` at minimum.
 *
 * Side-effect free — unit-tested in `ledger-filters.test.ts`.
 */
import { LEDGER_PAGE_SIZE } from "./constants";

export interface LedgerFilters {
  readonly entryType: string | null;
  readonly venueId: string;
  readonly currency: string;
  /** ISO date (YYYY-MM-DD) or empty. */
  readonly from: string;
  readonly to: string;
  /** 1-indexed. */
  readonly page: number;
}

// Note: the API also supports `propertyId` / `subscriptionId` filters; they're
// intentionally deferred from the v1 UI (add here + in `LedgerPage` when needed).

export function buildLedgerQuery(filters: LedgerFilters): string {
  const params = new URLSearchParams();

  if (filters.entryType) params.set("entryType", filters.entryType);

  const venue = filters.venueId.trim();
  if (venue) params.set("venueId", venue);

  const currency = filters.currency.trim();
  if (currency) params.set("currency", currency.toLowerCase());

  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);

  if (filters.page > 1) params.set("page", String(filters.page));
  params.set("limit", String(LEDGER_PAGE_SIZE));

  return `?${params.toString()}`;
}
